<script setup lang="tsx">
import { computed, onBeforeMount, onBeforeUnmount, ref } from 'vue'
import { commonProps } from './props'
import ClearButton from './ClearButton.vue'
import Multiselect from 'vue-multiselect'
import type { Response, Instance } from '@/models/api-generated'
import http from '@/services/http'
import type { Column } from '@/stores/column'
import { viewing } from '@/services/utils'

const isLoading = ref(false)
const isOpen = ref(false)
const model = defineModel<Column>({ required: true })
const props = defineProps(commonProps)
const options = ref<Instance[]>([])

const internalModel = computed<Instance[]>({
	get() {
		if (viewing.value) return [options.value.find((opt) => opt.id === viewing.value)]

		return model.value.filter.map((item: any) => {
			const matchingOption = options.value.find((opt) => opt.id === item)
			return matchingOption && { id: matchingOption.id, name: matchingOption.name }
		})
	},
	set(value: Instance[]) {
		if (viewing.value) return

		model.value.filter = value.map((x) => x.id)
	},
})

onBeforeUnmount(() => {
	if (!viewing.value) model.value.filter = []
})
onBeforeMount(() => {
	if (viewing.value) model.value.filter = [viewing.value]

	search('')
})

async function search(q: string) {
	isLoading.value = true

	const { data } = await http.get<Response<Instance[]>>('api/instance/search?q=' + q)

	options.value = data.data ?? []

	isLoading.value = false
}
</script>

<template>
	<Multiselect
		:multiple="true"
		:show-labels="false"
		:hide-selected="true"
		:close-on-select="false"
		:clear-on-select="false"
		:id="props.id"
		:disabled="props.disabled || viewing > 0"
		:placeholder="model.label + '...'"
		:options="options"
		:internal-search="false"
		:preserve-search="true"
		:show-no-results="false"
		:loading="isLoading"
		v-model="internalModel"
		label="name"
		track-by="id"
		:limit="isOpen ? 100 : 3"
		@search-change="search"
		@open="isOpen = true"
		@close="isOpen = false"
	>
		<template #clear>
			<ClearButton right="2rem" v-if="internalModel.length" @click="internalModel = []"></ClearButton>
		</template>

		<template v-if="!isOpen" #limit>
			<strong class="multiselect__strong">{{ $t('misc.limit', [internalModel.length - 3]) }}</strong>
		</template>
	</Multiselect>
</template>
